/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../widgets/livewidget/nls/StringResources',
	'./QueryServiceUtils',
	'underscore'
], (StringResources, QueryServiceUtils, _) => {
	class InternalQueryServiceErrors {
		constructor(dashboard) {
			this.dashboard = dashboard;
		}

		handleQueryError({ error, promptsState, visualization, content, isPreview=false }, errorResponse) {
			error = error || errorResponse && errorResponse.error;
			const prompts = this.dashboard.getFeature('Prompts');
			const dataSource = visualization.getDataSource();
			const isPromptFault = prompts && prompts.isPromptFault(error);
			const promptSpecList = isPromptFault && prompts.getPromptSpecList(error);
			if (isPromptFault && !promptsState.hasInValidState(promptSpecList)) {
				promptsState.updatePromptsState(promptSpecList);
				return prompts.openPromptView(promptSpecList, visualization, isPreview).then(responseList => {
					return this._resolvePromptValues(responseList, visualization);
				}).catch(error => {
					return Promise.reject(error);
				});
			} else {
				// TODO: We should eventually use 'getName' when it's implemented (it will be synchronous)
				// instead of using 'getLocalizedName' (which is async)
				return dataSource.getLocalizedName().then(name => {
					return Promise.reject(this._queryFailed(content, name, error));
				}).catch((error) => {
					return Promise.reject(error);
				});
			}
		}

		_resolvePromptValues(responseList, visualization) {
			let aPromptInfo = [];
			const queryProperties = ['name', 'values', 'dataType', 'capabilities', 'modelFilterItem'];

			_.each(responseList, (promptResponse) => {
				const promptResult = _.pick(promptResponse, queryProperties);
				aPromptInfo.push(promptResult);
			});

			// Prepare query spec values based on prompt dataType
			const promptValueSpecs = this._mergeAndResetSavedPrompts(aPromptInfo, visualization);
			_.each(promptValueSpecs, (promptSpec) => {
				if (!promptSpec.capabilities.optional) {
					// MUN based dataType expect a useValue while all other types expect a displayValue for a parameter.
					promptSpec.values = QueryServiceUtils.getPromptValues(promptSpec.values, promptSpec.dataType);
				}
			});

			return promptValueSpecs;
		}

		_mergeAndResetSavedPrompts(currPromptSpecs, visualization) {
			let newSpecs = null;
			// Newly resolved prompt values
			if (currPromptSpecs && currPromptSpecs.length) {
				newSpecs = {};
				currPromptSpecs.forEach(currPromptSpec => {
					newSpecs[currPromptSpec.name] = currPromptSpec;
				});
			}

			// Saved prompt values
			const savedPrompts = visualization.getSavedPrompts();
			const promptSpecList =  savedPrompts.getPromptSpecList();
			promptSpecList.forEach(promptSpec => {
				if (!newSpecs[promptSpec.name]) {
					newSpecs[promptSpec.name] = promptSpec;
				}
			});

			// Reset to null if prompts are all deleted or reset with merged values.
			savedPrompts.reset(newSpecs);
			return newSpecs ? JSON.parse(JSON.stringify(Object.values(newSpecs))) : null;
		}

		_queryFailed(content, sourceName, failure) {

			let hasUnavailableMetadataColumns = false;

			if (failure.message === 'promptingIsDisabled') {
				// this failure will be handled in Explore
				return failure;
			}
			let msg = 'dwErrorRunningQuery';
			let code;
			if (failure.reason === 'staleRequest') {
				msg = 'dwErrorStaleRequest';
			} else if (failure.reason === 'geoQueryFail') {
				msg = 'dwErrorGeoData';
			} else if (failure.reason === 'cancelPromptSignon') {
				msg = 'dwPromptSignonCancelWarning';
			} else if (failure.reason === 'unSupportedPromptType') {
				msg = failure.reason;
			} else if (failure.responseJSON && failure.responseJSON.errors) {
				const oErr = failure.responseJSON.errors[0];
				code = oErr && oErr.code;
				if (code === 'DSS-GEN-0002') {
					hasUnavailableMetadataColumns = true;
				} else if (code === 'DSS-GEN-0001') {
					msg = StringResources.get('errorSourceNotFound', { sourceName });
				} else if (code === 'XQE-PLN-0226') {
					msg = StringResources.get('errorActionNotSupported', { sourceName });
				} else if (code === 'XQE-PLN-0214') {
					msg = StringResources.get('errorMeasureNotSupportedOnMultiEdges');
				} else if (code === 'XQE-MDX-0020') {
					msg = StringResources.get('errorExceedAllowableValue');
				} else if (oErr && oErr.message) {
					msg = oErr.message;
				}
			}

			if (failure.reason !== 'staleRequest') {
				const state = content.getFeature('state.internal');
				state && state.setError(Object.assign(new Error(), { msg, code }));
			}

			const param = {
				'datasetName': sourceName
			};

			return Object.assign(new Error(), {
				msg,
				param,
				hasUnavailableMetadataColumns,
				errorInfo: failure
			});
		}
	}

	return InternalQueryServiceErrors;
});
